import React, { useEffect, useState, useRef } from 'react';
import { FaBell, FaTimes } from 'react-icons/fa';
import { useNotifications } from '../../contexts/notifications';
import './styles.css';

const NotificationsDropdown = () => {
  const { notificacoes, marcarComoLida } = useNotifications();
  const [isOpen, setIsOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);

  // Detectar se está em mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Prevenir scroll do body quando dropdown está aberto no mobile
  useEffect(() => {
    if (isMobile && isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobile, isOpen]);

  const handleMarcarComoLida = async (notificacaoId) => {
    try {
      await marcarComoLida(notificacaoId);
    } catch (error) {
      console.error('Erro ao marcar notificação como lida:', error);
    }
  };

  const handleToggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const handleCloseDropdown = () => {
    setIsOpen(false);
  };

  // Fechar dropdown quando clicar fora - melhorado para mobile
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isOpen && 
          dropdownRef.current && 
          !dropdownRef.current.contains(event.target) &&
          buttonRef.current &&
          !buttonRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    const handleTouchStart = (event) => {
      handleClickOutside(event);
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleTouchStart);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleTouchStart);
    };
  }, [isOpen]);

  const formatarTempo = (timestamp) => {
    const agora = Date.now();
    const diferenca = agora - timestamp;
    const minutos = Math.floor(diferenca / (1000 * 60));
    const horas = Math.floor(diferenca / (1000 * 60 * 60));
    const dias = Math.floor(diferenca / (1000 * 60 * 60 * 24));

    if (minutos < 1) return 'Agora';
    if (minutos < 60) return `${minutos}min`;
    if (horas < 24) return `${horas}h`;
    return `${dias}d`;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'DENTRO': return 'text-green-400';
      case 'DUVIDA': return 'text-yellow-400';
      case 'FORA': return 'text-red-400';
      default: return 'text-gray-400';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'DENTRO': return 'confirmou presença';
      case 'DUVIDA': return 'está em dúvida';
      case 'FORA': return 'não vai comparecer';
      default: return status;
    }
  };

  return (
    <div className={`relative notifications-dropdown ${isMobile && isOpen ? 'mobile-open' : ''}`}>
      <button
        ref={buttonRef}
        onClick={handleToggleDropdown}
        className="relative p-3 lg:p-2.5 text-gray-400 hover:text-white hover:bg-gray-700/50 
          active:bg-gray-700/70 rounded-xl transition-all duration-300 group
          touch-manipulation min-w-[44px] min-h-[44px] flex items-center justify-center"
        aria-label="Notificações"
        aria-expanded={isOpen}
      >
        <FaBell className="w-5 h-5 transition-transform duration-300 group-hover:scale-110 
          group-active:scale-95" />
        {notificacoes.length > 0 && (
          <span className="absolute -top-1 -right-1 bg-blue-500 text-white text-xs font-bold 
            rounded-full w-5 h-5 flex items-center justify-center animate-pulse
            min-w-[18px] min-h-[18px]">
            {notificacoes.length > 9 ? '9+' : notificacoes.length}
          </span>
        )}
      </button>

      {isOpen && (
        <>
          {/* Overlay para mobile */}
          {isMobile && (
            <div 
              className="mobile-overlay"
              onClick={handleCloseDropdown}
            />
          )}
          
          <div 
            ref={dropdownRef}
            className={`${isMobile ? 'notification-dropdown-content' : 
              'absolute right-0 mt-2 w-80 max-w-[95vw]'} 
              bg-gray-800 rounded-lg shadow-2xl py-2 z-50 border border-gray-700 
              max-h-[80vh] overflow-y-auto transform transition-all duration-200 ease-out 
              origin-top-right animate-in slide-in-from-top-2 fade-in notification-list`}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-700">
              <h3 className="text-white font-medium text-base">Notificações</h3>
              <button
                onClick={handleCloseDropdown}
                className="text-gray-400 hover:text-white active:text-gray-300 transition-colors
                  p-2 rounded-lg hover:bg-gray-700/50 active:bg-gray-700/70
                  touch-manipulation min-w-[36px] min-h-[36px] flex items-center justify-center"
                aria-label="Fechar notificações"
              >
                <FaTimes className="w-4 h-4" />
              </button>
            </div>

            {notificacoes.length === 0 ? (
              <div className="px-4 py-8 text-center">
                <FaBell className="w-10 h-10 text-gray-600 mx-auto mb-3" />
                <p className="text-gray-400 font-medium mb-1">Nenhuma notificação</p>
                <p className="text-gray-500 text-sm px-2 leading-relaxed">
                  Você será notificado quando alguém confirmar presença nos seus grupos
                </p>
              </div>
            ) : (
              <div className="divide-y divide-gray-700">
                {notificacoes.map((notificacao) => (
                  <div
                    key={notificacao.id}
                    className="px-4 py-4 hover:bg-gray-700/50 active:bg-gray-700/70 
                      transition-colors relative group touch-manipulation notification-item"
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 w-2 h-2 bg-blue-500 rounded-full mt-3"></div>
                      
                      <div className="flex-1 min-w-0 pr-2">
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-white font-medium text-sm truncate notification-text">
                            {notificacao.grupoNome || 'Grupo'}
                          </p>
                          <span className="text-gray-500 text-xs flex-shrink-0 ml-2">
                            {formatarTempo(notificacao.timestamp)}
                          </span>
                        </div>
                        
                        <p className="text-gray-300 text-sm leading-relaxed notification-text">
                          <span className="font-medium">{notificacao.usuarioNome}</span>
                          {' '}
                          <span className={getStatusColor(notificacao.status)}>
                            {getStatusText(notificacao.status)}
                          </span>
                        </p>
                      </div>

                      <button
                        onClick={() => handleMarcarComoLida(notificacao.id)}
                        className="opacity-70 group-hover:opacity-100 p-2 text-gray-400 hover:text-white 
                          active:text-gray-300 transition-all duration-200 rounded-lg
                          hover:bg-gray-600/50 active:bg-gray-600/70
                          touch-manipulation min-w-[36px] min-h-[36px] flex items-center justify-center
                          lg:opacity-0"
                        title="Marcar como lida"
                        aria-label="Marcar notificação como lida"
                      >
                        <FaTimes className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default NotificationsDropdown;
